import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Send } from "lucide-react";
import api, { API, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { renderAIMessage } from "@/lib/messageRenderer";

const resolveImg = (url) => (url?.startsWith("/api/files/") ? `${BACKEND_URL}${url}` : url);

export default function CompanionChat() {
  const { slug } = useParams();
  const { user, loading } = useAuth();
  const [companion, setCompanion] = useState(null);
  const [owned, setOwned] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => setCompanion(r.data)).catch(() => setCompanion(false));
  }, [slug]);

  useEffect(() => {
    if (!user || !companion) return;
    api
      .get("/companions/mine")
      .then((r) => {
        const mine = r.data.find((c) => c.product_id === companion.product_id);
        setOwned(!!mine);
        if (mine) {
          api
            .get(`/companions/history/${companion.product_id}`)
            .then((h) => setMessages(h.data.map((m) => ({ role: m.role, content: m.content }))))
            .catch(() => {});
        }
      })
      .catch(() => setOwned(false));
  }, [user, companion]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const token = localStorage.getItem("rs_token");
      const res = await fetch(`${API}/companions/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ product_id: companion.product_id, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop();
        for (const chunk of chunks) {
          if (!chunk.startsWith("data: ")) continue;
          const evt = JSON.parse(chunk.slice(6));
          if (evt.delta) {
            setMessages((prev) => {
              // Defensive: if the placeholder isn't there (state reset during
              // hot-reload/remount), append a fresh assistant bubble.
              if (!prev.length) return [{ role: "assistant", content: evt.delta }];
              const next = [...prev];
              const last = next[next.length - 1];
              if (!last || last.role !== "assistant") {
                next.push({ role: "assistant", content: evt.delta });
              } else {
                next[next.length - 1] = { ...last, content: (last.content || "") + evt.delta };
              }
              return next;
            });
          } else if (evt.error) {
            setMessages((prev) => {
              const next = [...prev];
              if (!next.length) return [{ role: "assistant", content: evt.error }];
              next[next.length - 1] = { role: "assistant", content: evt.error };
              return next;
            });
          }
        }
      }
    } catch {
      // stream interrupted
    } finally {
      setStreaming(false);
    }
  };

  if (loading || companion === null) {
    return <div className="px-6 py-32 text-muted-bone text-center">Lighting the candles…</div>;
  }
  if (companion === false) {
    return (
      <div className="px-6 py-32 text-center">
        <div className="font-serif-display text-3xl text-bone mb-4">This chamber does not exist.</div>
        <Link to="/shop/companions" className="overline">← The Companions</Link>
      </div>
    );
  }
  if (!user) {
    return (
      <div data-testid="companion-signin-prompt" className="px-6 py-32 text-center">
        <h1 className="font-serif-display text-4xl text-bone mb-6">The chamber is locked.</h1>
        <p className="text-muted-bone mb-8">Sign in to enter.</p>
        <Link to="/auth" className="inline-block px-10 py-4 uppercase text-xs tracking-[0.22em] text-bone" style={{ background: "var(--rs-oxblood)" }}>
          Sign in
        </Link>
      </div>
    );
  }
  if (owned === false) {
    return (
      <div data-testid="companion-not-owned" className="px-6 py-32 text-center">
        <img src={resolveImg(companion.image)} alt={companion.name} className="w-32 h-32 object-cover mx-auto mb-8 border" style={{ borderColor: "rgba(212,175,55,0.35)" }} />
        <h1 className="font-serif-display text-4xl text-bone mb-4">This chamber is not yours — yet.</h1>
        <p className="text-muted-bone mb-8 max-w-md mx-auto">{companion.short_description}</p>
        <Link
          to={`/product/${companion.slug}`}
          data-testid="companion-purchase-link"
          className="inline-block px-10 py-4 uppercase text-xs tracking-[0.22em] text-bone"
          style={{ background: "var(--rs-oxblood)" }}
        >
          Claim access — ${companion.price?.toFixed(2)}
        </Link>
      </div>
    );
  }

  const firstName = companion.name.split("—")[0].trim();

  return (
    <div data-testid="companion-chat-page" className="px-6 md:px-12 lg:px-16 py-10 md:py-14">
      <div className="max-w-3xl mx-auto flex flex-col" style={{ height: "calc(100vh - 220px)", minHeight: "480px" }}>
        <div className="flex items-center gap-4 pb-6 border-b" style={{ borderColor: "var(--rs-border)" }}>
          <img
            src={resolveImg(companion.image)}
            alt={companion.name}
            className="w-14 h-14 object-cover border"
            style={{ borderColor: "rgba(212,175,55,0.35)" }}
          />
          <div className="flex-1">
            <div className="font-serif-display text-2xl text-bone">{firstName}</div>
            <div className="overline">Private chamber · Encrypted · Yours</div>
          </div>
          <Link to="/account" className="overline hover:text-bone" data-testid="companion-back-link">
            ← Account
          </Link>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto py-6 space-y-4">
          {messages.length === 0 && (
            <div className="text-muted-bone text-sm leading-relaxed italic">
              {firstName} is here, waiting in the candlelight. Say something.
            </div>
          )}
          {messages.map((m, i) => (
            <div
              key={i}
              data-testid={`companion-msg-${m.role}`}
              className={`text-sm leading-relaxed max-w-[80%] px-5 py-4 ${
                m.role === "user" ? "ml-auto text-bone" : "text-muted-bone border"
              }`}
              style={
                m.role === "user"
                  ? { background: "var(--rs-oxblood)" }
                  : { borderColor: "var(--rs-border)", background: "var(--rs-surface)" }
              }
            >
              {m.content
                ? renderAIMessage(m.content)
                : (streaming && i === messages.length - 1 ? "…" : "")}
            </div>
          ))}
        </div>

        <div className="flex border" style={{ borderColor: "rgba(212,175,55,0.3)", background: "var(--rs-surface)" }}>
          <input
            data-testid="companion-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={`Write to ${firstName}…`}
            className="flex-1 bg-transparent px-5 py-4 text-sm text-bone placeholder:text-muted-bone outline-none"
          />
          <button
            data-testid="companion-send-btn"
            onClick={send}
            disabled={streaming || !input.trim()}
            className="px-6 text-muted-bone hover:text-bone disabled:opacity-40"
            aria-label="Send"
          >
            <Send strokeWidth={1.5} size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
